import './perks-post-comment-form.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { useHashLocation } from 'wouter-preact/use-hash-location';

import { IconMessageArrowUpRight } from '../../../../../common/icons/icon-message-arrow-up-right';
import { useTranslation } from '../../../../../i18n';
import { NeedAccessButton } from '../../../../../need-access/components/need-access-button/need-access-button.component';
import { NeedAccessContentType } from '../../../../../need-access/need-access.interface';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import type { Comment } from '../../../../model/comment.interface';
import { CommentState } from '../../../../model/comment.interface';
import type { Post } from '../../../../model/post.interface';
import { selectUser } from '../../../../store/perks.selectors';
import { postComment } from '../../../../store/perks.thunk';
import { PerksAvatar } from '../../../perks-avatar/perks-avatar.component';
import { COMPACT_AVATAR_BADGE_SIZE_PX } from '../../../perks-badge/perks-badge.component';
import { scrollToComment } from '../comment-entry';

interface PerksPostCommentFormProps {
  post: Post;
  parentComment?: Comment;
  displayAvatar?: boolean;
  placeholder?: string;
  /** Focus the textarea when this instance mounts, if it's actually rendered. */
  focusOnMount?: boolean;
}

export const PerksPostCommentForm = ({
  post,
  parentComment,
  displayAvatar = false,
  placeholder,
  focusOnMount = false,
}: PerksPostCommentFormProps) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('perks.addComment');
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [commentValue, setCommentValue] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // useRef, not createRef: the submit handler reads this after awaiting the
  // post, by which point setIsSubmitting has re-rendered — a per-render ref
  // would already be detached (null) and the height reset would be skipped.
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const vvHandlerRef = useRef<(() => void) | null>(null);
  const location = useHashLocation();

  useEffect(() => {
    if (inputWrapperRef.current) {
      const h = inputWrapperRef.current.offsetHeight;
      // 0 when the input is hidden (a non-member on mobile); pinning the button to 0px would then
      // flatten it if the viewport later widens and the input comes back
      if (h > 0) {
        inputWrapperRef.current.parentElement?.style.setProperty('--initial-form-height', `${h}px`);
      }
    }
  }, []);

  useEffect(() => {
    if (!focusOnMount) return;
    const el = commentTextareaRef.current;
    // Skip when this instance is display:none — the inline reply form on
    // phones, where the fixed bar takes the reply instead.
    if (el && el.getClientRects().length > 0) el.focus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('comment') === 'true') {
      setTimeout(() => {
        requestAnimationFrame(() => {
          focusAndScrollToTextarea();
        });
      }, 150);
    }
  }, [location, parentComment]);

  const focusAndScrollToTextarea = () => {
    const el = commentTextareaRef.current;
    if (!el) return;

    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });

    setTimeout(() => {
      el.focus();
    }, 300);
  };

  const handleFocus = () => {
    document.body.classList.add('keyboard-open');
    if (!window.visualViewport) return;
    const vv = window.visualViewport;
    const handler = () => {
      const stack = document.getElementById('single-mobile-stack');
      if (!stack) return;
      const stackHeight = stack.offsetHeight || 60;
      // Position element's bottom edge 8px above the visual viewport bottom.
      // Using vv.height directly avoids the window.innerHeight discrepancy on iOS.
      const top = vv.offsetTop + vv.height - stackHeight - 8;
      stack.style.top = `${Math.max(0, top)}px`;
      stack.style.bottom = 'auto';
    };
    vvHandlerRef.current = handler;
    vv.addEventListener('resize', handler);
    vv.addEventListener('scroll', handler);
    handler();
  };

  const handleBlur = () => {
    document.body.classList.remove('keyboard-open');
    if (window.visualViewport && vvHandlerRef.current) {
      window.visualViewport.removeEventListener('resize', vvHandlerRef.current);
      window.visualViewport.removeEventListener('scroll', vvHandlerRef.current);
      vvHandlerRef.current = null;
    }
    const stack = document.getElementById('single-mobile-stack');
    if (stack) {
      stack.style.top = '';
      stack.style.bottom = '';
    }
  };

  const adjustHeight = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    const newHeight = Math.max(36, Math.min(textarea.scrollHeight, 80));
    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY = textarea.scrollHeight > 80 ? 'scroll' : 'hidden';
  };

  const handleTextChange = (e: any) => {
    const target = e.target as HTMLTextAreaElement;
    const value = target.value;

    setCommentValue(value);
    setIsDirty(true);
    setHasError(!value || value.trim() === '');

    adjustHeight(target);
  };

  const handlePostComment = async () => {
    // Guard against duplicate submissions: ignore re-entry while a post is
    // already in flight (slow endpoint + no feedback led users to re-tap,
    // creating duplicate comments — SNGL-6940).
    if (isSubmitting || !commentValue || !commentValue.trim()) {
      return;
    }

    const comment: Comment = {
      id: null,
      textContent: commentValue,
      user: null,
      state: CommentState.ACTIVE,
      userLiked: false,
      createdAt: new Date(),
      hasChildren: false,
      children: null,
    };

    setIsSubmitting(true);
    try {
      const result = await dispatch(
        postComment({ postId: post.id, parentCommentId: parentComment?.id || null, comment })
      );
      setCommentValue('');
      setIsDirty(false);
      setHasError(false);

      if (commentTextareaRef.current) {
        commentTextareaRef.current.style.height = '36px';
        commentTextareaRef.current.style.overflowY = 'hidden';
        commentTextareaRef.current.blur();
      }

      if (postComment.fulfilled.match(result) && result.payload?.id) {
        scrollToComment(result.payload.id);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isInvalid = isDirty && hasError;
  const isDisabled = isDirty && hasError;

  return (
    <div
      class={`single-comment-form-wrapper${post?.userHasAccess ? '' : ' single-comment-form-no-access'}`}
    >
      {displayAvatar && user && (
        <PerksAvatar user={user} size={36} badgeSize={COMPACT_AVATAR_BADGE_SIZE_PX} />
      )}

      <div
        class={`single-comment-input-wrapper ${isInvalid ? 'has-error' : ''}`}
        ref={inputWrapperRef}
      >
        <textarea
          name="commentInput"
          aria-label={resolvedPlaceholder}
          ref={commentTextareaRef}
          rows={1}
          required
          // a non-member cannot post, so the field is only a prompt beside Join to Comment -- not
          // somewhere to type out a comment they then lose
          disabled={!post?.userHasAccess}
          placeholder={resolvedPlaceholder}
          value={commentValue}
          onInput={handleTextChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>

      {post?.userHasAccess ? (
        <button
          type="submit"
          class="btn button"
          disabled={isDisabled || isSubmitting}
          onPointerDown={(e) => e.preventDefault()}
          onClick={handlePostComment}
        >
          <span>{t('perks.postCommentButton')}</span>
          <IconMessageArrowUpRight />
        </button>
      ) : (
        <NeedAccessButton
          contentType={NeedAccessContentType.POST}
          contentId={post.id}
          showLock={false}
          message={t('perks.joinToComment')}
        />
      )}
    </div>
  );
};
